import { Box, VStack, Heading, type BoxProps } from '@chakra-ui/react';
import { Card } from './Card';
import { FixtureListGrid } from '../../components/FixtureListGrid';
import { excludeFixturesWithLiveMatches } from '@/helpers/fixtures/liveMatchDisplay';
import type { Fixture } from '@/models';
import type { LiveMatch } from '@/models/liveMatch';

interface FixtureListCardProps extends Omit<BoxProps, 'children'> {
  fixtures: Fixture[];
  liveMatches: LiveMatch[];
  maxW?: BoxProps['maxW'];
  /** Symmetric horizontal padding for matchday headers and match rows. */
  listPadX?: BoxProps['px'];
  /** When true (desktop column), list scrolls inside the card; when false, page scrolls. */
  scrollContained?: boolean;
  /** When false, live rows are omitted here (e.g. pinned elsewhere on mobile). */
  showLiveInline?: boolean;
  hideHeading?: boolean;
  singleLineTeamNames?: boolean;
}

export function FixtureListCard({
  fixtures,
  liveMatches,
  maxW,
  listPadX = 0,
  scrollContained = true,
  showLiveInline = true,
  hideHeading = false,
  singleLineTeamNames = false,
  ...cardProps
}: FixtureListCardProps) {
  const fixtureList = showLiveInline ? fixtures : excludeFixturesWithLiveMatches(fixtures, liveMatches);
  const list = (
    <FixtureListGrid
      fixtureList={fixtureList}
      liveMatches={showLiveInline ? liveMatches : []}
      svg
      nameLines={singleLineTeamNames ? 1 : undefined}
    />
  );

  const heading = hideHeading ? null : (
    <Heading fontSize='lg' flexShrink={0}>
      Fixtures
    </Heading>
  );

  if (!scrollContained) {
    return (
      <Card
        maxW={maxW}
        w='full'
        px={listPadX}
        bg='transparent'
        py={0}
        {...cardProps}
      >
        <VStack spacing={4} align='stretch'>
          {heading}
          {list}
        </VStack>
      </Card>
    );
  }

  return (
    <Card
      maxW={maxW}
      w='full'
      px={listPadX}
      borderRadius={0}
      bg='transparent'
      py={0}
      display='flex'
      flexDirection='column'
      minH={0}
      {...cardProps}
    >
      <Box flex={1} minH={0} display='flex' flexDirection='column' overflow='hidden'>
        <VStack flex={1} minH={0} spacing={4} align='stretch'>
          {heading}
          <Box
            flex={1}
            minH={0}
            overflowY='auto'
            overflowX='hidden'
            sx={{ scrollbarGutter: 'stable' }}
          >
            {list}
          </Box>
        </VStack>
      </Box>
    </Card>
  );
}
